import { Injectable, inject } from '@angular/core';
import { Company } from '@bdeo-motor/smartphoto-entities';
import { BehaviorSubject, Observable, map, filter } from 'rxjs';
import { AuthService } from './auth.service';
import { BdeoSelectOption } from '@bdeo/webcomponents';
import { CompanyRepositoryService, SelectorItems } from './company-repository.service';
import { I18nService } from '@bdeo-motor/angular-i18n';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly companyRepositoryService = inject(CompanyRepositoryService);
  private readonly i18nService = inject(I18nService);
  private readonly authService = inject(AuthService);
  private readonly company$ = new BehaviorSubject<Company | undefined>(undefined);
  private company?: Company;

  protected readonly settledCompany$ = this.company$.pipe(filter((company): company is Company => Boolean(company)));
  public readonly hasDeadline$ = this.settledCompany$.pipe(map((company) => company?.deadlineSelfadjust));
  public readonly hasAdditionals$ = this.settledCompany$.pipe(map((company) => company.smartphoto?.additionals));
  public readonly hasGoToAdditionalsButton$ = this.settledCompany$.pipe(map((company) => company.brokersInfo?.showGoToAdditionalsButton));
  public readonly canEditEstimation$ = this.settledCompany$.pipe(map((company) => company.brokersInfo?.canEditEstimation));
  public readonly manageDamages$ = this.settledCompany$.pipe(map((company) => company.brokersInfo?.manageDamages));
  public readonly cardFields$ = this.settledCompany$.pipe(map((company) => company.brokersInfo?.cardFields));
  public readonly infoFields$ = this.settledCompany$.pipe(map((company) => company.brokersInfo?.infoFields));
  public readonly createFields$ = this.settledCompany$.pipe(map((company) => company.createFields));
  public readonly useConditionsSelfadjust$ = this.settledCompany$.pipe(map((company) => company.useConditionsSelfadjust));

  public async getCompany(): Promise<Company> {
    try {
      if (this.company) {
        return this.company;
      }

      this.company = await this.companyRepositoryService.getCompany();
      this.company$.next(this.company);
      await this.configureCompanyLanguage(this.company);

      return this.company;
    } catch (e: unknown) {
      this.authService.logOut();
      throw e;
    }
  }

  private async configureCompanyLanguage(company: Company): Promise<void> {
    const language = company.fixedLanguage ? company.fixedLanguage : navigator.language ?? 'es-ES';

    await this.i18nService.loadLanguages([language]);
    await this.i18nService.changeLanguage(language);
    if (company.labels['brokers']) {
      this.i18nService.addResourceBundle(language, 'brokers', company.labels['brokers'], true, true);
    }
  }

  public getCurrentCompany(): Company | undefined {
    return this.company;
  }

  public getCurrentCompanyObservable(): Observable<Company | undefined> {
    return this.company$.asObservable();
  }

  public getNavBarLogo(): string {
    return this.company?.styles?.selfadjust?.images?.logo_brokersNavbar ?? '';
  }

  public async getSelector(name: string): Promise<SelectorItems> {
    return this.companyRepositoryService.getSelector(name);
  }

  public async getSelectorValues(selectorID: string): Promise<BdeoSelectOption[]> {
    const rawSelectorValues = await this.getSelector(selectorID);

    if (!Array.isArray(rawSelectorValues)) {
      throw new Error('Unexpected selector format');
    }

    return rawSelectorValues.map((option) => ({
      value: option?.value,
      text: option?.name ?? '',
      disabled: false,
      selected: false
    }));
  }
}
